import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { api } from '../services/api';
import SafeIcon from '../common/SafeIcon';

const Assignments = () => {
  const { data, refreshData, t } = useAppContext();
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [assignedProductIds, setAssignedProductIds] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (selectedCustomerId) {
      const assigned = data.customerProducts
        .filter(cp => String(cp.customer_id) === String(selectedCustomerId))
        .map(cp => String(cp.product_id));
      setAssignedProductIds(new Set(assigned));
    } else {
      setAssignedProductIds(new Set());
    }
  }, [selectedCustomerId, data.customerProducts]);

  const toggleProduct = (productId) => {
    setAssignedProductIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) newSet.delete(productId);
      else newSet.add(productId);
      return newSet;
    });
  };

  const handleSave = async () => {
    if (!selectedCustomerId) return;
    setIsSaving(true);
    await api.saveCustomerProducts(selectedCustomerId, Array.from(assignedProductIds));
    await refreshData();
    setIsSaving(false);
  };

  const activeProducts = data.products.filter(p => p.active);
  const selectedCustomer = data.customers.find(c => String(c.id) === String(selectedCustomerId));

  /**
   * Fixed: Standalone Portal URL Generation
   * Points to /portal/#/{slug} instead of admin app path
   */
  const orderUrl = selectedCustomer?.slug 
    ? `${window.location.origin}/portal/#/${selectedCustomer.slug}` 
    : '';

  return (
    <div className="space-y-6 flex flex-col h-[calc(100vh-4rem)]">
      <div className="flex justify-between items-end shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('assignments.title')}</h1>
          <p className="text-gray-500 mt-1">{t('assignments.subtitle')}</p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6 flex-1 min-h-0">
        {/* Customer List */}
        <div className="w-full md:w-1/3 bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col shadow-sm">
          <div className="p-4 bg-gray-50 border-b border-gray-200 font-semibold text-gray-700">{t('assignments.selectCustomer')}</div>
          <div className="overflow-y-auto flex-1 p-2 space-y-1">
            {data.customers.filter(c => c.active).map(c => (
              <button 
                key={c.id} 
                onClick={() => setSelectedCustomerId(c.id)}
                className={`w-full text-left p-3 rounded-lg text-sm transition-colors ${selectedCustomerId === c.id ? 'bg-amber-100 text-amber-900 font-medium' : 'hover:bg-gray-50 text-gray-700'}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        {/* Product Assignment List */}
        <div className="w-full md:w-2/3 bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col shadow-sm">
          <div className="p-4 bg-gray-50 border-b border-gray-200 font-semibold text-gray-700 flex justify-between items-center">
            <span>{t('assignments.availableProducts')}</span>
            {selectedCustomerId && (
              <button 
                onClick={handleSave} 
                disabled={isSaving}
                className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-1.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
              >
                {isSaving ? t('actions.saving') : t('assignments.save')}
              </button>
            )}
          </div>

          {selectedCustomerId && (
            <div className="px-4 py-3 bg-amber-50 border-b border-amber-100 flex items-center justify-between animate-fade-in">
              <div className="flex-1 mr-4 overflow-hidden">
                <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">{t('assignments.orderLink')}</p>
                <p className="text-xs font-mono text-gray-600 truncate">{orderUrl || 'No slug assigned'}</p>
              </div>
              {orderUrl && (
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(orderUrl);
                    alert('Portal link copied to clipboard!');
                  }}
                  className="p-1.5 hover:bg-amber-200 rounded text-amber-700 transition-colors shrink-0"
                  title="Copy Link"
                >
                  <SafeIcon name="Copy" className="w-4 h-4" />
                </button>
              )}
            </div>
          )}

          <div className="overflow-y-auto flex-1 p-4">
            {!selectedCustomerId ? (
              <div className="h-full flex items-center justify-center text-gray-400 italic">
                {t('assignments.placeholder')}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {activeProducts.map(p => {
                  const isAssigned = assignedProductIds.has(String(p.id));
                  return (
                    <div 
                      key={p.id} 
                      onClick={() => toggleProduct(p.id)}
                      className={`p-3 rounded-xl border-2 cursor-pointer transition-all flex items-center justify-between ${isAssigned ? 'border-amber-500 bg-amber-50' : 'border-gray-100 bg-white hover:border-gray-300'}`}
                    >
                      <div>
                        <p className={`font-medium text-sm ${isAssigned ? 'text-amber-900' : 'text-gray-800'}`}>{p.name}</p>
                        <p className="text-xs text-gray-500">{p.category}</p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isAssigned ? 'bg-amber-500 border-amber-500' : 'border-gray-300'}`}>
                        {isAssigned && <SafeIcon name="Check" className="text-white w-3 h-3" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Assignments;